import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Sparkles, Quote, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { currentLang } from "@/lib/lang";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
  } catch { return iso; }
}

export default function Devotional() {
  const [today, setToday] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const lang = currentLang();
    let retryTimer = null;

    const load = async (attempt = 0) => {
      try {
        const [t, a] = await Promise.all([
          api.get(`/devotional/today?language=${lang}`),
          api.get(`/devotional/archive?limit=30&language=${lang}`),
        ]);
        setToday(t.data);
        setArchive(a.data.filter((d) => d.date !== t.data.date));

        // If the backend served a stale (past-date) devotional, quietly poll
        // again a few times — the server-side pre-warm should replace it
        // within a few seconds after a cold cache miss.
        const todayIso = new Date().toISOString().slice(0, 10);
        const isStale = !!t.data?.stale || (t.data?.date && t.data.date !== todayIso);
        if (isStale && attempt < 6) {
          retryTimer = setTimeout(() => load(attempt + 1), 4000);
        }
      } catch {
        /* keep the loading indicator; retry on manual reload */
      } finally {
        if (attempt === 0) setLoading(false);
      }
    };

    load();
    return () => { if (retryTimer) clearTimeout(retryTimer); };
  }, []);

  return (
    <div data-testid="devotional-page">
      <PageHero
        overline="Daily Devotional"
        title={<>A word for <span className="font-accent text-gold-gradient italic">today.</span></>}
        subtitle="A fresh Spirit-led devotional every morning — scripture, reflection, and prayer to sharpen your faith and lead your day."
      />

      <section className="mx-auto max-w-[1100px] px-6 md:px-10 pb-24">
        {loading ? (
          <div className="min-h-[300px] flex items-center justify-center text-white/50" data-testid="devotional-loading">
            Preparing today's devotional…
          </div>
        ) : today ? (
          <Reveal>
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative border border-gold-500/25 bg-ink-paper/40 p-8 md:p-14 corner-frame"
              data-testid="devotional-today"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Sparkles size={18} className="text-gold-500" />
                  <span className="text-xs uppercase tracking-[0.25em] text-gold-400">
                    {fmtDate(today.date)}
                  </span>
                </div>
                <BookOpen size={22} className="text-gold-500/60" strokeWidth={1.4} />
              </div>

              <h2 className="mt-8 font-serif text-4xl md:text-5xl leading-tight text-white" data-testid="devotional-title">
                {today.title}
              </h2>

              <div className="mt-8 border-l-2 border-gold-500/60 pl-6" data-testid="devotional-scripture">
                <p className="text-[11px] uppercase tracking-[0.3em] text-gold-400 mb-2">
                  {today.scripture_reference}
                </p>
                <p className="font-accent italic text-2xl md:text-3xl text-white/90 leading-snug">
                  "{today.scripture_text}"
                </p>
              </div>

              <div className="mt-10 space-y-6 text-white/75 leading-relaxed md:text-[17px]" data-testid="devotional-reflection">
                {today.reflection.split(/\n+/).map((p, i) => (
                  <p key={i}>{p.trim()}</p>
                ))}
              </div>

              <div className="mt-10 border border-gold-500/25 bg-ink p-6 md:p-8" data-testid="devotional-prayer">
                <div className="flex items-center gap-3 mb-3">
                  <Quote size={16} className="text-gold-500" />
                  <span className="text-xs uppercase tracking-[0.25em] text-gold-400">Today's prayer</span>
                </div>
                <p className="font-accent italic text-lg md:text-xl text-white/85 leading-relaxed">
                  {today.prayer}
                </p>
              </div>

              {today.stale && (
                <p className="mt-6 text-xs text-gold-400/80 italic" data-testid="devotional-stale">
                  A fresh word for today is being prepared — refreshing automatically…
                </p>
              )}
            </motion.article>
          </Reveal>
        ) : (
          <p className="text-white/50 py-14 text-center border border-gold-500/15">
            Devotional is warming up — please refresh in a moment.
          </p>
        )}

        {/* Archive */}
        {archive.length > 0 && (
          <div className="mt-24" data-testid="devotional-archive">
            <Reveal className="mb-8 flex items-center justify-between">
              <Overline>Previous devotionals</Overline>
              <span className="text-xs text-white/40 tracking-[0.15em]">{archive.length} entries</span>
            </Reveal>
            <div className="space-y-3">
              {archive.map((d) => (
                <div
                  key={d.id}
                  className="border border-gold-500/15 bg-ink-paper/30 overflow-hidden"
                  data-testid={`archive-item-${d.date}`}
                >
                  <button
                    onClick={() => setOpenId(openId === d.id ? null : d.id)}
                    className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left hover:bg-ink-paper/60 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">{fmtDate(d.date)}</p>
                      <p className="mt-1 font-serif text-lg text-white truncate">{d.title}</p>
                      <p className="text-xs text-white/50 mt-0.5">{d.scripture_reference}</p>
                    </div>
                    <ChevronDown
                      size={18}
                      className={`text-gold-400 shrink-0 transition-transform duration-300 ${openId === d.id ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openId === d.id && (
                    <div className="border-t border-gold-500/15 px-6 py-6 space-y-4 text-white/70 text-sm leading-relaxed">
                      <p className="font-accent italic text-white/85 text-base">"{d.scripture_text}"</p>
                      <p>{d.reflection}</p>
                      <p className="font-accent italic text-gold-300/90">{d.prayer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
